import { useSlideOverContext } from '@/hooks/useSlideOverContext';
import { useModalContext } from '@/hooks/useModalContext';
import { FlowFormData } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/types';
import { Button } from '@/lib';
import { getLocalStorageItem, setLocalStorageItem } from '@/utils/localstorage';
import { FC, PropsWithChildren, type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ComuniTopic } from '../../constants';
import { ComuniChannel, SYSTEM_MODELS, SystemModel } from '../../templates/catalog';
import type { WorkspaceTemplateRow } from '../../templates/partitionWorkspaceTemplates';
import { topicOfTemplateName } from '../../templates/topicPrefix';
import type { EditFlowSelection } from './editFlow';
import { DeleteWorkspaceTemplateModal } from './DeleteWorkspaceTemplateModal';
import { ModelloPreviewSlideOver } from './ModelloPreviewSlideOver';
import { ModelliStatsRow } from './ModelliStatsRow';
import { ModelliWorkspaceLists } from './MyModels';
import { NewModelChannelDialog } from './NewModelChannelDialog';
import { SilentTemplateDelete } from './SilentTemplateDelete';
import { TemplateFlowEditor, type TemplateFlowRole } from './TemplateFlowEditor';
import { useModelliWaStatus } from './useModelliWaStatus';
import { WaOnboardingBanner } from './WaOnboardingBanner';
import { WaZoneErrorBanner } from './WaZoneErrorBanner';
import { WaZoneLoading } from './WaZoneLoading';
import { workspaceRowToEditSelection } from './workspaceRowEditSelection';

type EditingState = {
  channel: ComuniChannel;
  role: 'templateInsert' | 'templateEdit';
  modelId?: string;
  editSelection?: EditFlowSelection;
  /** Topic group prefix locked for Customize / Edit (hidden in form, attached on submit). */
  lockedTopic?: ComuniTopic;
};

const VALID_ROLES: EditingState['role'][] = ['templateInsert', 'templateEdit'];

const readPersistedEditing = (): EditingState | null => {
  try {
    const raw = getLocalStorageItem('comuni', 'personalizza');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      channel?: ComuniChannel;
      role?: TemplateFlowRole;
      modelId?: string;
      editSelection?: EditFlowSelection;
      lockedTopic?: ComuniTopic;
    };
    if (!parsed?.channel || !['whatsapp', 'sms', 'email'].includes(parsed.channel)) return null;
    // Drop legacy templateDelete sessions — delete is confirm-modal only now.
    if (parsed.role === 'templateDelete') return null;
    if (!VALID_ROLES.includes((parsed.role ?? 'templateInsert') as EditingState['role'])) return null;
    return {
      channel: parsed.channel,
      role: (parsed.role as EditingState['role']) ?? 'templateInsert',
      modelId: parsed.modelId,
      editSelection: parsed.editSelection,
      lockedTopic: parsed.lockedTopic,
    };
  } catch {
    return null;
  }
};

type ModelliTabChromeProps = PropsWithChildren<{
  onCreateClick: () => void;
}>;

const ModelliTabChrome: FC<ModelliTabChromeProps> = ({ onCreateClick, children }) => {
  const { t } = useTranslation('comuni');

  return (
    // grid-cols-1 (= minmax(0,1fr)) instead of an implicit auto column: truncated preview
    // lines are whitespace-nowrap, so an auto track would size to their full unwrapped width
    <div className='grid min-w-0 max-w-full grid-cols-1 gap-6'>
      <div className='flex items-start justify-between gap-4'>
        <div className='min-w-0'>
          <h2 className='text-xl font-medium text-primary-900'>{t('modelli.title')}</h2>
          <p className='mt-1 text-sm text-tertiary-600'>{t('modelli.subtitle')}</p>
        </div>
        <Button intent='primary' size='md' className='flex-none' onClick={onCreateClick} text={`+ ${t('modelli.createNew')}`} />
      </div>
      {children}
    </div>
  );
};

export const ModelliTab: FC = () => {
  const { t } = useTranslation('comuni');
  const { openSlideOver } = useSlideOverContext();
  const { openModal } = useModalContext();
  const {
    isConnectorsLoading,
    isWaTemplatesLoading,
    isWaTemplatesError,
    isWaZoneSettling,
    needsWaOnboarding,
    waOnboardingUrl,
    reloadWaTemplates,
    statusById,
    stats,
    waTemplates,
  } = useModelliWaStatus();

  const [editing, setEditing] = useState<EditingState | null>(readPersistedEditing);
  const [pendingDelete, setPendingDelete] = useState<EditFlowSelection | null>(null);
  const [channelDialogOpen, setChannelDialogOpen] = useState(false);

  const startEditing = (state: EditingState) => {
    setLocalStorageItem('comuni', 'personalizza', JSON.stringify(state));
    setEditing(state);
  };
  const exitEditing = () => {
    setLocalStorageItem('comuni', 'personalizza', '');
    setEditing(null);
  };

  const openPreview = (model: SystemModel) =>
    openSlideOver({
      renderContent: ModelloPreviewSlideOver,
      renderContentProps: { model: { channel: model.channel, name: model.name, formData: model.formData } },
      showButtonInsideSlideOver: true,
    });

  const startWorkspaceEdit = (row: WorkspaceTemplateRow) =>
    startEditing({
      channel: row.channel,
      role: 'templateEdit',
      editSelection: workspaceRowToEditSelection(row),
      lockedTopic: topicOfTemplateName(row.name) ?? undefined,
    });

  const startWorkspaceDelete = (row: WorkspaceTemplateRow) => {
    openModal({
      renderContent: DeleteWorkspaceTemplateModal,
      renderContentProps: {
        editSelection: workspaceRowToEditSelection(row),
        onDeleteConfirmed: (selection: EditFlowSelection) => setPendingDelete(selection),
      },
    });
  };

  if (pendingDelete) {
    return (
      <SilentTemplateDelete
        selection={pendingDelete}
        onExit={() => {
          setPendingDelete(null);
          void reloadWaTemplates();
        }}
      />
    );
  }

  if (editing) {
    const model = editing.role === 'templateInsert' && editing.modelId ? SYSTEM_MODELS.find((item) => item.id === editing.modelId) : undefined;
    const editorInitialData = model
      ? model.channel === 'whatsapp'
        ? { step2: model.formData as FlowFormData }
        : { step1: model.formData as FlowFormData }
      : undefined;

    const editorTitle =
      editing.role === 'templateEdit'
        ? t('modelli.editor.editTitle')
        : model
          ? t('modelli.editor.customizeTitle', { name: model.name })
          : t('modelli.editor.createTitle');

    return (
      <TemplateFlowEditor
        key={`${editing.role}-${editing.channel}-${editing.modelId ?? editing.editSelection?.channel ?? 'new'}`}
        channel={editing.channel}
        role={editing.role}
        title={editorTitle}
        initialFormData={editorInitialData}
        editSelection={editing.editSelection}
        lockedTopic={editing.lockedTopic ?? (editing.role === 'templateInsert' ? model?.topic : undefined)}
        onExit={exitEditing}
      />
    );
  }

  const waZoneStatus: ReactNode = isWaZoneSettling ? (
    <WaZoneLoading />
  ) : needsWaOnboarding ? (
    <WaOnboardingBanner onboardingUrl={waOnboardingUrl} />
  ) : isWaTemplatesError ? (
    <WaZoneErrorBanner onRetry={reloadWaTemplates} />
  ) : null;

  return (
    <ModelliTabChrome onCreateClick={() => setChannelDialogOpen(true)}>
      {channelDialogOpen ? (
        <NewModelChannelDialog
          onClose={() => setChannelDialogOpen(false)}
          onConfirm={(channel) => {
            setChannelDialogOpen(false);
            startEditing({ channel, role: 'templateInsert', lockedTopic: undefined });
          }}
        />
      ) : null}

      {waZoneStatus}

      <ModelliStatsRow stats={stats} />

      <ModelliWorkspaceLists
        waTemplates={waTemplates}
        connectorsLoading={isConnectorsLoading}
        waTemplatesLoading={isWaTemplatesLoading}
        waTemplatesError={isWaTemplatesError}
        onRetryWa={reloadWaTemplates}
        statusById={statusById}
        statusSkeleton={isWaZoneSettling}
        onPreviewSystem={openPreview}
        onCustomize={(model) => startEditing({ channel: model.channel, role: 'templateInsert', modelId: model.id, lockedTopic: model.topic })}
        onEditWorkspace={startWorkspaceEdit}
        onDeleteWorkspace={startWorkspaceDelete}
      />
    </ModelliTabChrome>
  );
};
